import { EyeInvisibleOutlined, EyeOutlined, UserOutlined } from "ui";
import type { ChangeEvent, CSSProperties, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTE_URLS } from "../../constants/consts";
import { useMemo, useRef, useState } from "react";
import {
  clearLocalStorage,
  changePasswordBySession,
} from "../../api/sessionManagement";
import { clearUserLanguageCache } from "../../hooks/useUserLanguage.hook";
import { useTranslation } from "react-i18next";
import { SuccessModal } from "./components/successModal";
import { UIButton, UIIconButton, UIInput, UIModal } from "ui";
import { useOutsideClick } from "../../hooks/useOutsideClick.hook";

const HTTP_STATUS_FORBIDDEN = 403;

type NavButtonPersonProps = {
  menuMode?: "popup" | "inline";
  triggerContent?: ReactNode;
  triggerClassName?: string;
  menuClassName?: string;
};

export function NavButtonPerson({
  menuMode = "popup",
  triggerContent,
  triggerClassName,
  menuClassName,
}: NavButtonPersonProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuWrapperRef = useRef<HTMLDivElement>(null);
  const isInlineMenu = menuMode === "inline";
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();

  const [openModal, setOpenModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [repeatNewPassword, setRepeatNewPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showRepeat, setShowRepeat] = useState(false);

  const [errors, setErrors] = useState<{
    current?: string;
    new?: string;
    repeat?: string;
  }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const passwordComplexity = (password: string) =>
    /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$/.test(password);

  const validateCurrentPassword = (): string | undefined => {
    if (!currentPassword) return t("navbar.current_password");
    return undefined;
  };

  const validateNewPassword = (): string | undefined => {
    if (!newPassword) return t("navbar.new_password");
    if (!passwordComplexity(newPassword)) return t("navbar.password_complexity");
    if (newPassword === currentPassword) return t("navbar.password_same_as_current");
    return undefined;
  };

  const validateRepeatPassword = (): string | undefined => {
    if (!repeatNewPassword) return t("navbar.repeat_new_password");
    if (newPassword !== repeatNewPassword) return t("navbar.passwords_do_not_match");
    return undefined;
  };

  const validate = () => {
    const newErrors = {
      current: validateCurrentPassword(),
      new: validateNewPassword(),
      repeat: validateRepeatPassword(),
    };

    const filteredErrors = Object.fromEntries(
      Object.entries(newErrors).filter(([_, value]) => value)
    );

    setErrors(filteredErrors);
    return Object.keys(filteredErrors).length === 0;
  };

  const handleChange = async () => {
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const result = await changePasswordBySession(currentPassword, newPassword);
      if (result && result.logout) {
        setShowSuccessModal(true);
      } else {
        setTimeout(() => {
          handleModalClose();
        }, 1200);
      }
    } catch (error: any) {
      if (error?.status === HTTP_STATUS_FORBIDDEN) {
        setErrors((prev) => ({
          ...prev,
          current: t("navbar.invalid_password"),
        }));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangePassword = () => {
    setIsMenuOpen(false);
    setOpenModal(true);
  };

  const handleLogout = () => {
    setIsMenuOpen(false);
    clearLocalStorage();
    clearUserLanguageCache();
    const browserLang = navigator.language || "en-GB";
    i18n.changeLanguage(browserLang);
    window.dispatchEvent(new Event("storage"));
    navigate(ROUTE_URLS.SIGN_IN);
  };

  const handleModalClose = () => {
    setOpenModal(false);
    setCurrentPassword("");
    setNewPassword("");
    setRepeatNewPassword("");
    setShowCurrent(false);
    setShowNew(false);
    setShowRepeat(false);
    setErrors({});
  };

  const closeSuccessModal = () => {
    setShowSuccessModal(false);
  };

  const shouldDisableSaveButton = useMemo(
    () => isSubmitting || !currentPassword || !newPassword || !repeatNewPassword,
    [isSubmitting, currentPassword, newPassword, repeatNewPassword]
  );

  useOutsideClick(menuWrapperRef, () => setIsMenuOpen(false), isMenuOpen);

  return (
    <>
      <div ref={menuWrapperRef} style={{ position: "relative" }}>
        {triggerContent ? (
          <button
            type="button"
            aria-label="person"
            aria-expanded={isMenuOpen}
            onClick={() => setIsMenuOpen((prev) => !prev)}
            className={triggerClassName}
            style={customTriggerStyle}
          >
            {triggerContent}
          </button>
        ) : (
          <UIIconButton aria-label="person" onClick={() => setIsMenuOpen((prev) => !prev)}>
            <UserOutlined />
          </UIIconButton>
        )}
        {isInlineMenu ? (
          <div
            className={menuClassName}
            data-open={isMenuOpen ? "true" : "false"}
            style={{
              ...inlineMenuStyle,
              marginTop: isMenuOpen ? 8 : 0,
              maxHeight: isMenuOpen ? 124 : 0,
              opacity: isMenuOpen ? 1 : 0,
              transform: `translateY(${isMenuOpen ? "0" : "-8px"})`,
              pointerEvents: isMenuOpen ? "auto" : "none",
              borderWidth: isMenuOpen ? 1 : 0,
              boxShadow: isMenuOpen ? inlineMenuStyle.boxShadow : "none",
            }}
          >
            <UIButton
              type="button"
              variant="text"
              onClick={handleChangePassword}
              style={menuItemStyle}
            >
              {t("navbar.change_password")}
            </UIButton>
            <UIButton
              type="button"
              variant="text"
              onClick={handleLogout}
              style={menuItemStyle}
            >
              {t("logout")}
            </UIButton>
          </div>
        ) : isMenuOpen ? (
          <div
            className={menuClassName}
            style={{
              ...popupMenuStyle,
              marginTop: 8,
            }}
          >
            <UIButton
              type="button"
              variant="text"
              onClick={handleChangePassword}
              style={menuItemStyle}
            >
              {t("navbar.change_password")}
            </UIButton>
            <UIButton
              type="button"
              variant="text"
              onClick={handleLogout}
              style={menuItemStyle}
            >
              {t("logout")}
            </UIButton>
          </div>
        ) : null}
      </div>

      <SuccessModal showSuccessModal={showSuccessModal} closeModal={closeSuccessModal} />

      <UIModal open={openModal} onClose={handleModalClose} width={460} centered destroyOnClose>
        <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 12 }}>
          {t("navbar.change_password")}
        </div>

        <PasswordField
          label={t("navbar.current_password")}
          value={currentPassword}
          onChange={(event) => {
            setCurrentPassword(event.target.value);
            setErrors((prev) => ({ ...prev, current: undefined }));
          }}
          shown={showCurrent}
          onToggle={() => setShowCurrent((prev) => !prev)}
          error={errors.current}
          t={t}
        />

        <PasswordField
          label={t("navbar.new_password")}
          value={newPassword}
          onChange={(event) => {
            setNewPassword(event.target.value);
            setErrors((prev) => ({ ...prev, new: undefined }));
          }}
          shown={showNew}
          onToggle={() => setShowNew((prev) => !prev)}
          error={errors.new}
          t={t}
        />

        <PasswordField
          label={t("navbar.repeat_new_password")}
          value={repeatNewPassword}
          onChange={(event) => {
            setRepeatNewPassword(event.target.value);
            setErrors((prev) => ({ ...prev, repeat: undefined }));
          }}
          shown={showRepeat}
          onToggle={() => setShowRepeat((prev) => !prev)}
          error={errors.repeat}
          t={t}
        />

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <UIButton onClick={handleModalClose} disabled={isSubmitting}>
            {t("navbar.cancel")}
          </UIButton>
          <UIButton
            variant="contained"
            onClick={handleChange}
            disabled={shouldDisableSaveButton}
          >
            {isSubmitting ? `${t("navbar.save")}...` : t("navbar.save")}
          </UIButton>
        </div>
      </UIModal>
    </>
  );
}

const menuItemStyle: CSSProperties = {
  width: "100%",
  border: "none",
  background: "transparent",
  textAlign: "left",
  cursor: "pointer",
  padding: "10px 12px",
};

const PasswordField = ({
  label,
  value,
  onChange,
  shown,
  onToggle,
  error,
  t,
}: {
  label: string;
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  shown: boolean;
  onToggle: () => void;
  error?: string;
  t: (key: string) => string;
}) => (
  <div style={{ marginTop: 10 }}>
    <UIInput
      fullWidth
      label={label}
      type={shown ? "text" : "password"}
      value={value}
      onChange={onChange}
      suffix={
        <UIButton
          type="button"
          variant="text"
          onClick={onToggle}
          aria-label={shown ? t("navbar.hide_password") : t("navbar.show_password")}
          style={{ border: "none", background: "transparent", cursor: "pointer" }}
        >
          {shown ? <EyeOutlined /> : <EyeInvisibleOutlined />}
        </UIButton>
      }
    />
    {error ? <div style={{ color: "#ff4d4f", marginTop: 4, fontSize: 12 }}>{error}</div> : null}
  </div>
);

const customTriggerStyle: CSSProperties = {
  border: "none",
  background: "transparent",
  padding: 0,
  cursor: "pointer",
  width: "100%",
  textAlign: "left",
};

const popupMenuStyle: CSSProperties = {
  position: "absolute",
  right: 0,
  top: "100%",
  minWidth: 180,
  border: "1px solid #e5e7eb",
  borderRadius: 8,
  backgroundColor: "#fff",
  boxShadow: "0 6px 18px rgba(0,0,0,0.1)",
  zIndex: 50,
};

const inlineMenuStyle: CSSProperties = {
  border: "1px solid #e5e7eb",
  borderRadius: 8,
  backgroundColor: "#fff",
  boxShadow: "0 6px 18px rgba(0,0,0,0.1)",
  overflow: "hidden",
  transition: "max-height 0.28s ease, opacity 0.22s ease, transform 0.22s ease, margin-top 0.22s ease",
};

export default NavButtonPerson;
